// usePreloadNodeEngagements.js
import { useEffect } from 'react';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useEngagement } from 'context/EngagementContext';
import { useUser } from 'context/UserContext';

// Utilities
import { EngagementType } from 'utilities/Enums';

import { subscribeToEngagements } from 'services/engagements/EngagementReadService';

/**
 * Custom hook to load engagements for a specific item.
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - Skip Firestore when false (visitors).
 */
const usePreloadNodeEngagements = ({ enabled = true } = {}) => {

  // Global Context
  const { currentNode } = useNavigation();
  const {
    setFavorites,
    setComments,
    setCommentLikes,
    setCommentDislikes,
    setLikes,
    setShares,
  } = useEngagement();

  // User Context
  const { user } = useUser();

  const currentNodeKey = currentNode?.key;
  const userKey = user?.key;

  useEffect(() => {

    if (!enabled || !userKey || !currentNodeKey) {
      // clear everything when deps are missing
      setComments([]);
      setCommentLikes([]);
      setCommentDislikes([]);
      setLikes([]);
      setFavorites([]);
      setShares([]);
      return;
    }

    /**
     * handleUpdate
     *
     * Handles update.
     *
     * @param {unknown} engagements - engagements.
     */
    const handleUpdate = (engagements) => {
      
      // ----- COMMENTS (clone before sort; emit fresh object refs) -----
      const commentsOnly = engagements.filter(
        (e) => e.type === EngagementType.NODE_COMMENT
      );

      const sortedComments = [...commentsOnly].sort((a, b) => {
        const ad = a?.dateCreated?.toDate?.() ?? 0;
        const bd = b?.dateCreated?.toDate?.() ?? 0;
        return ad - bd;
      });

      // clone each item so field changes (e.g., text/deleted) surface as new refs
      setComments(sortedComments.map(c => ({ ...c })));

      // ----- COMMENT LIKES/DISLIKES -----
      setCommentLikes(
        engagements.filter((e) => e.type === EngagementType.COMMENT_LIKE)
      );
      setCommentDislikes(
        engagements.filter((e) => e.type === EngagementType.COMMENT_DISLIKE)
      );

      // ----- NODE LIKES -----
      setLikes(
        engagements.filter((e) => e.type === EngagementType.NODE_LIKE)
      );

      // ----- FAVORITES -----
      setFavorites(
        engagements.filter((e) => e.type === EngagementType.NODE_FAVORITE)
      );

      // ----- SHARES -----
      setShares(
        engagements.filter((e) => e.type === EngagementType.NODE_SHARE)
      );
    };

    const params = [
      { field: 'itemKey', operator: '==', value: currentNodeKey },
    ];

    const unsubscribe = subscribeToEngagements({ params, sortFields: [] }, handleUpdate);

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };

    // depend on stable primitives to avoid unnecessary re-subscribes
  }, [
    currentNodeKey,
    enabled,
    userKey,
    setComments,
    setCommentLikes,
    setCommentDislikes,
    setLikes,
    setFavorites,
    setShares
  ]);

};

export default usePreloadNodeEngagements;
